// Blackboard — Certificate PDF generation utilities
// Ported 1:1 from certificateUtils.js (Firebase Blackboard app)

import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

export interface CertificateData {
  studentName: string;
  trainingName: string;
  completionDate: string;
  certCode: string;
  orgName?: string;
  logoBase64?: string;
}

/** Generate a random 12-character alphanumeric certificate code */
export function generateCertCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let code = '';
  for (let i = 0; i < 12; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/** Build the full verification URL for a certificate code */
export function getVerificationUrl(certCode: string): string {
  return `${window.location.origin}/verify/${certCode}`;
}

/** Generate a landscape letter-size PDF certificate */
export async function generateCertificatePDF(data: CertificateData): Promise<jsPDF> {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'in', format: 'letter' });
  const pageW = 11;
  const pageH = 8.5;

  // --- Navy border ---
  const borderColor: [number, number, number] = [25, 0, 155]; // var(--color-primary-900)
  const goldColor: [number, number, number] = [166, 158, 3];

  // Outer border
  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.06);
  doc.rect(0.3, 0.3, pageW - 0.6, pageH - 0.6);

  // Inner border
  doc.setLineWidth(0.02);
  doc.rect(0.45, 0.45, pageW - 0.9, pageH - 0.9);

  // Corner accents
  const cornerLen = 0.5;
  doc.setDrawColor(...goldColor);
  doc.setLineWidth(0.04);
  const corners: [number, number, number, number, number, number][] = [
    // top-left
    [0.45, 0.45 + cornerLen, 0.45, 0.45, 0.45 + cornerLen, 0.45],
    // top-right
    [pageW - 0.45 - cornerLen, 0.45, pageW - 0.45, 0.45, pageW - 0.45, 0.45 + cornerLen],
    // bottom-left
    [0.45, pageH - 0.45 - cornerLen, 0.45, pageH - 0.45, 0.45 + cornerLen, pageH - 0.45],
    // bottom-right
    [pageW - 0.45 - cornerLen, pageH - 0.45, pageW - 0.45, pageH - 0.45, pageW - 0.45, pageH - 0.45 - cornerLen],
  ];
  for (const [x1, y1, x2, y2, x3, y3] of corners) {
    doc.line(x1, y1, x2, y2);
    doc.line(x2, y2, x3, y3);
  }

  // --- Logo (if provided) ---
  let yPos = 1.2;
  if (data.logoBase64) {
    try {
      doc.addImage(data.logoBase64, 'PNG', pageW / 2 - 0.5, 0.7, 1, 1);
      yPos = 1.9;
    } catch {
      // skip logo on error
    }
  }

  // --- Heading ---
  doc.setTextColor(...borderColor);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text('CERTIFICATE OF COMPLETION', pageW / 2, yPos, { align: 'center' });

  // --- Decorative line ---
  yPos += 0.25;
  doc.setDrawColor(...goldColor);
  doc.setLineWidth(0.02);
  doc.line(pageW / 2 - 2, yPos, pageW / 2 + 2, yPos);

  // --- "This certifies that" ---
  yPos += 0.45;
  doc.setTextColor(80, 80, 80);
  doc.setFontSize(11);
  doc.text('This certifies that', pageW / 2, yPos, { align: 'center' });

  // --- Student name ---
  yPos += 0.45;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(data.studentName, pageW / 2, yPos, { align: 'center' });

  // --- Underline ---
  yPos += 0.08;
  const nameWidth = doc.getTextWidth(data.studentName) / 72; // approximate
  doc.setDrawColor(...goldColor);
  doc.setLineWidth(0.015);
  doc.line(pageW / 2 - nameWidth * 14, yPos, pageW / 2 + nameWidth * 14, yPos);

  // --- "has successfully completed" ---
  yPos += 0.45;
  doc.setTextColor(80, 80, 80);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('has successfully completed', pageW / 2, yPos, { align: 'center' });

  // --- Training name ---
  yPos += 0.45;
  doc.setTextColor(...borderColor);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(data.trainingName, pageW / 2, yPos, { align: 'center' });

  // --- Date ---
  yPos += 0.55;
  doc.setTextColor(80, 80, 80);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Date of Completion: ${data.completionDate}`, pageW / 2, yPos, { align: 'center' });

  // --- Org name ---
  if (data.orgName) {
    yPos += 0.35;
    doc.setFontSize(10);
    doc.text(data.orgName, pageW / 2, yPos, { align: 'center' });
  }

  // --- QR code (centered, large) ---
  try {
    const verifyUrl = getVerificationUrl(data.certCode);
    const qrDataUrl = await QRCode.toDataURL(verifyUrl, { width: 300, margin: 1 });
    const qrSize = 1.8;
    const qrX = pageW / 2 - qrSize / 2;
    yPos += 0.3;
    doc.addImage(qrDataUrl, 'PNG', qrX, yPos, qrSize, qrSize);

    // "Scan to verify" label
    yPos += qrSize + 0.2;
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('Scan to verify', pageW / 2, yPos, { align: 'center' });

    // Verification URL
    yPos += 0.2;
    doc.setFontSize(7);
    doc.setTextColor(74, 130, 196);
    doc.text(verifyUrl, pageW / 2, yPos, { align: 'center' });
  } catch {
    // skip QR on error
  }

  // --- Certificate code footer ---
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text(`Certificate Code: ${data.certCode}`, 0.6, pageH - 0.55);

  return doc;
}
